import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Users, FileText, BarChart2, Settings, Bell, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

// ── Shortcut di navigazione statici ──────────────────────────────────────
const NAV_SHORTCUTS = [
    { id: 'nav-dashboard',    label: 'Dashboard',          path: '/app/dashboard',   icon: BarChart2, group: 'Naviga' },
    { id: 'nav-clients',      label: 'Clienti',            path: '/app/clients',     icon: Users,     group: 'Naviga' },
    { id: 'nav-comparatore',  label: 'Comparatore CTE',    path: '/app/comparatore', icon: Zap,       group: 'Naviga' },
    { id: 'nav-items',        label: 'Contratti',          path: '/app/clients',     icon: FileText,  group: 'Naviga' },
    { id: 'nav-notif',        label: 'Notifiche',          path: '/app/notifications', icon: Bell,    group: 'Naviga' },
    { id: 'nav-settings',     label: 'Impostazioni',       path: '/app/settings',    icon: Settings,  group: 'Naviga' },
];

const HIGHLIGHT = (text, query) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: 'rgba(124,58,237,0.4)', color: '#c4b5fd', borderRadius: 3, padding: '0 2px' }}>
                {text.slice(idx, idx + query.length)}
            </mark>
            {text.slice(idx + query.length)}
        </>
    );
};

export default function CommandPalette({ open, onClose }) {
    const { effectiveProfile: profile } = useAuth();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(0);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    // Focus input quando si apre
    useEffect(() => {
        if (open) {
            setQuery('');
            setResults([]);
            setSelected(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Chiudi con Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    // Navigazione tastiera
    useEffect(() => {
        const handler = (e) => {
            if (!open) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)); }
            if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
            if (e.key === 'Enter') { e.preventDefault(); if (results[selected]) handleSelect(results[selected]); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, results, selected]);

    // Ricerca con debounce 300ms
    const search = useCallback(async (q) => {
        if (!q.trim()) {
            setResults(NAV_SHORTCUTS);
            return;
        }
        setLoading(true);
        try {
            const orgId = profile?.organization_id;
            const [clientsRes, offersRes] = await Promise.all([
                orgId
                    ? supabase.from('clients')
                        .select('id, first_name, last_name, company_name')
                        .eq('organization_id', orgId)
                        .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,company_name.ilike.%${q}%`)
                        .limit(5)
                    : { data: [] },
                supabase.from('offers')
                    .select('id, name, supplier_id')
                    .ilike('name', `%${q}%`)
                    .limit(5),
            ]);

            const clients = (clientsRes.data || []).map(c => ({
                id:    `client-${c.id}`,
                label: c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                path:  `/app/clients/${c.id}`,
                icon:  Users,
                group: 'Clienti',
            }));

            const offers = (offersRes.data || []).map(o => ({
                id:    `offer-${o.id}`,
                label: o.name,
                path:  `/app/comparatore`,
                icon:  Zap,
                group: 'Offerte',
            }));

            const navMatches = NAV_SHORTCUTS.filter(n =>
                n.label.toLowerCase().includes(q.toLowerCase())
            );

            setResults([...navMatches, ...clients, ...offers]);
            setSelected(0);
        } catch (e) {
            console.error('[CommandPalette] search error:', e);
        } finally {
            setLoading(false);
        }
    }, [profile?.organization_id]);

    const handleQueryChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(v), 300);
    };

    const handleSelect = (item) => {
        navigate(item.path);
        onClose();
    };

    // Mostra shortcut di default quando query vuota
    useEffect(() => {
        if (open && !query) setResults(NAV_SHORTCUTS);
    }, [open, query]);

    if (!open) return null;

    // Raggruppa per group
    const groups = results.reduce((acc, item) => {
        const g = item.group || 'Altro';
        if (!acc[g]) acc[g] = [];
        acc[g].push(item);
        return acc;
    }, {});
    let flatIdx = 0;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(6px)',
                    zIndex: 9999,
                }}
            />

            {/* Dialog */}
            <div style={{
                position: 'fixed',
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: 600,
                zIndex: 10000,
                borderRadius: 16,
                background: 'rgba(15,10,40,0.97)',
                border: '1px solid rgba(124,58,237,0.3)',
                boxShadow: '0 25px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
                overflow: 'hidden',
            }}>
                {/* Input */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                }}>
                    <Search size={18} color="rgba(255,255,255,0.4)" style={{ flexShrink: 0 }} />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={handleQueryChange}
                        placeholder="Cerca clienti, offerte, pagine..."
                        style={{
                            flex: 1, background: 'transparent', border: 'none', outline: 'none',
                            color: '#fff', fontSize: 16,
                        }}
                    />
                    {loading && (
                        <div style={{ width: 16, height: 16, border: '2px solid rgba(124,58,237,0.4)', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    )}
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 6, padding: '4px 8px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 11 }}>
                        ESC
                    </button>
                </div>

                {/* Risultati */}
                <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
                    {results.length === 0 && !loading && (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                            Nessun risultato per "{query}"
                        </div>
                    )}

                    {Object.entries(groups).map(([groupName, items]) => (
                        <div key={groupName}>
                            <div style={{ padding: '6px 20px 4px', fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                                {groupName}
                            </div>
                            {items.map((item) => {
                                const isSelected = flatIdx === selected;
                                const Icon = item.icon;
                                const currentIdx = flatIdx++;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleSelect(item)}
                                        onMouseEnter={() => setSelected(currentIdx)}
                                        style={{
                                            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '10px 20px', border: 'none', cursor: 'pointer', textAlign: 'left',
                                            background: isSelected ? 'rgba(124,58,237,0.15)' : 'transparent',
                                            borderLeft: isSelected ? '2px solid #7c3aed' : '2px solid transparent',
                                            transition: 'background 0.1s',
                                        }}
                                    >
                                        <div style={{
                                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                            background: isSelected ? 'rgba(124,58,237,0.25)' : 'rgba(255,255,255,0.06)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Icon size={15} color={isSelected ? '#a78bfa' : 'rgba(255,255,255,0.4)'} />
                                        </div>
                                        <span style={{ flex: 1, color: isSelected ? '#e2d9f3' : 'rgba(255,255,255,0.7)', fontSize: 14 }}>
                                            {HIGHLIGHT(item.label, query)}
                                        </span>
                                        {isSelected && <ArrowRight size={14} color="#7c3aed" />}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Footer hint */}
                <div style={{
                    padding: '8px 20px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', gap: 16,
                    color: 'rgba(255,255,255,0.25)', fontSize: 11,
                }}>
                    <span>↑↓ naviga</span>
                    <span>↵ apri</span>
                    <span>esc chiudi</span>
                </div>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </>
    );
}
